import { Enums, FeatureKeys } from "@/services/dropdown-enums";

import FeatureService from "@/services/feature-service";

// ---------------------------------------------------------------------------
// Plan-based limits (legacy — still applied for standard CRM users)
// ---------------------------------------------------------------------------

const PlanAllowedModules = {
  basic: ["lead", "appointments", "settings", "analytics"],
  pro: ["all"],
  premium: ["all"],
};

const PlanDeniedSubFeatures = {
  basic: [
    "lead.import",
    "lead.export",
    "lead.quickEdit",
    "analytics.dashboard.view",
    "analytics.dashboard.edit",
    "analytics.appointments-analytics.view",
    "analytics.appointments-analytics.edit",
  ],
  pro: ["lead.import"],
  premium: [],
};

const AllFeatures = [
  "analytics",
  "messaging",
  "conversation",
  "onBoarding",
  "lead",
  "list",
  "appointments",
  "automation",
  "sales",
  "settings",
];

const RESTRICTED_CRM_MODULES = [
  "messaging",
  "conversation",
  "onBoarding",
  "list",
  "automation",
  "sales",
];

const ORG_LEVEL_SETTINGS = [
  "settings.apiKeys",
  "settings.emailTemplate",
  "settings.emailSignature",
  "settings.advanceEmailSetting",
  "settings.generalEmailSetting",
  "settings.leadStatus",
  "settings.leadSource",
  "settings.leadTags",
  "settings.customField",
  "settings.leadScoring",
  "settings.leadSettings",
  "settings.taskStatus",
  "settings.taskType",
  "settings.taskPriority",
  "settings.taskReminder",
  "settings.smsTemplate",
  "settings.smsPreference",
  "settings.smsNumber",
  "settings.mediaStorage",
  "settings.rolesAndPermissions",
];

const APPOINTMENT_PERMISSION_PROFILES = {
  schedulingUser: {
    view: true,
    edit: true, // Can create/edit appointments on calendar
    dashboard: { view: true, edit: false },
    calendars: { view: true, edit: true },
    "appointment-scheduling": { view: true, edit: false },
    "appointment-members": { view: true, edit: false },
    "appointment-routing": { view: false, edit: false },
  },
  full: {
    view: true,
    edit: true,
    dashboard: { view: true, edit: true },
    calendars: { view: true, edit: true },
    "appointment-scheduling": { view: true, edit: true },
    "appointment-members": { view: true, edit: true },
    "appointment-routing": { view: true, edit: true },
  },
  readonly: {
    view: true,
    edit: false,
    dashboard: { view: true, edit: false },
    calendars: { view: true, edit: false },
    "appointment-scheduling": { view: true, edit: false },
    "appointment-members": { view: true, edit: false },
    "appointment-routing": { view: true, edit: false },
  },
};

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

const RoleEngine = (roleType, user = null) => {
  let roles;
  switch (roleType) {
    case Enums.Roles.CRMAdmin:
      roles = handleCRMAdmin(roleType, user);
      break;
    case Enums.Roles.CRMDefaultManager:
      roles = handleCRMDefaultManager(roleType, user);
      break;
    case Enums.Roles.SalesManager:
    case Enums.Roles.SalesManagerReadonly:
      roles = handleSalesManager(roleType, user);
      break;
    case Enums.Roles.SchedulingUser:
      roles = handleSchedulingUser();
      break;
    case Enums.Roles.SchedulingTeamManager:
      roles = handleSchedulingTeamManager();
      break;
    default:
      roles = handleSalesManager(roleType, user);
  }

  roles = applyAppointmentOnlyLimits(roles, user);

  if (!isAppointmentOnlyGated(user)) {
    roles = applySubscriptionPlanLimits(roles, user);
  }

  return roles;
};

// ---------------------------------------------------------------------------
// Role handlers
// ---------------------------------------------------------------------------

const handleCRMAdmin = (roleType, user) => {
  let roles = getDefaultRoles();
  roles = AdministratorPermissions.Get(roles, roleType);
  return roles;
};

const handleSalesManager = (roleType, user) => {
  let roles = getDefaultRoles();
  roles = applyReadOnlyRights(roles);

  if (roleType === Enums.Roles.SalesManager) {
    const allowedRoles = [
      "appointments",
      "lead",
      "list",
      "sales.tasks",
      "automation.automations",
      "automation.template",
    ];
    roles = allowGrantedRolesForEdit(roles, allowedRoles);
    roles = { ...roles, isSalesManager: true };
  }

  return roles;
};

const handleCRMDefaultManager = (roleType, user) => {
  let roles = getDefaultRoles();
  roles.analytics.view = false;
  roles.appointments.view = false;
  roles.appointments.edit = false;
  roles.analytics.dashboard.view = false;
  roles.analytics.dashboard.edit = false;
  roles.analytics.reports.view = false;
  roles.analytics.reports.edit = false;
  roles.automation.view = true;
  roles.automation.template.view = true;
  roles.settings.smsTemplate.view = true;
  roles.settings.apiKeys.view = true;
  roles.settings.emailTemplate.view = true;
  roles.sales.view = false;
  roles = { ...roles, isCRMDefaultManager: true };
  const allowedRoles = [
    "automation.template",
    "settings.smsTemplate",
    "settings.apiKeys",
    "settings.emailTemplate",
  ];
  roles = allowGrantedRolesForEdit(roles, allowedRoles);
  return roles;
};

const handleSchedulingUser = () => {
  const roles = getDefaultRoles();
  disableRestrictedCrmModules(roles);
  denyLeadAccess(roles);
  roles.appointments = buildAppointmentPermissions("schedulingUser");
  applyNoSettingsAccess(roles);
  disableAnalyticsEntirely(roles);
  return roles;
};

const handleSchedulingTeamManager = () => {
  const roles = getDefaultRoles();
  disableRestrictedCrmModules(roles);
  applyReadOnlyLeadPermissions(roles);
  roles.appointments = buildAppointmentPermissions("full");
  applyTeamManagerSettings(roles);
  disableAnalyticsEntirely(roles);
  return roles;
};

// ---------------------------------------------------------------------------
// Appointment-only gating
// ---------------------------------------------------------------------------

const isAppointmentOnlyGated = (user) => {
  const roleType = user?.RoleType;

  if (
    roleType === Enums.Roles.SchedulingUser ||
    roleType === Enums.Roles.SchedulingTeamManager
  ) {
    return true;
  }

  return needsAppointmentOnlyOverlay(user);
};

const needsAppointmentOnlyOverlay = (user) => {
  const activeFeatures = FeatureService.resolveActiveFeatures(user);
  const roleType = user?.RoleType;

  const isAppointmentOnlyFeature =
    activeFeatures.includes(FeatureKeys.APPOINTMENT_SCHEDULING) &&
    activeFeatures.length === 1;

  const isAppointmentOnlyCrmAdmin =
    roleType === Enums.Roles.CRMAdmin &&
    activeFeatures.includes(FeatureKeys.APPOINTMENT_SCHEDULING) &&
    !activeFeatures.includes(FeatureKeys.CRM);

  return isAppointmentOnlyFeature || isAppointmentOnlyCrmAdmin;
};

const applyAppointmentOnlyLimits = (roles, user) => {
  const roleType = user?.RoleType;

  // Dedicated handlers already applied full rules for scheduling roles.
  if (
    roleType === Enums.Roles.SchedulingUser ||
    roleType === Enums.Roles.SchedulingTeamManager
  ) {
    return roles;
  }

  if (!needsAppointmentOnlyOverlay(user)) {
    return roles;
  }

  disableRestrictedCrmModules(roles);
  denyLeadImport(roles);

  if (roleType === Enums.Roles.CRMAdmin) {
    applyAppointmentOnlyCrmAdminPermissions(roles);
  } else if (roleType === Enums.Roles.SalesManagerReadonly) {
    applyAppointmentOnlyReadonlyPermissions(roles);
  } else if (roleType === Enums.Roles.SalesManager) {
    applyAppointmentOnlyFallbackPermissions(roles);
  } else {
    // Safety net: unknown role with appointment-only features
    applyAppointmentOnlyFallbackPermissions(roles);
  }

  return roles;
};

// ---------------------------------------------------------------------------
// Shared permission helpers
// ---------------------------------------------------------------------------

const buildAppointmentPermissions = (profile) => ({
  ...APPOINTMENT_PERMISSION_PROFILES[profile],
});

const denyOrgSettings = (roles) => {
  ORG_LEVEL_SETTINGS.forEach((settingPath) => {
    roles = createNestedObjectFromStringAndApplyValue(
      roles,
      settingPath,
      false,
      "view"
    );
    roles = createNestedObjectFromStringAndApplyValue(
      roles,
      settingPath,
      false,
      "edit"
    );
  });
  roles.settings.userProfile = { view: true, edit: true };
  return roles;
};

const disableRestrictedCrmModules = (roles) => {
  RESTRICTED_CRM_MODULES.forEach((feature) => {
    if (roles[feature]) {
      forcefullyDisableNodeTree(roles[feature]);
    } else {
      createNestedObjectFromStringAndApplyValue(roles, feature, false, "view");
      createNestedObjectFromStringAndApplyValue(roles, feature, false, "edit");
    }
  });
};

const denyLeadImport = (roles) => {
  if (!roles.lead) roles.lead = {};
  roles.lead.import = { view: false, edit: false };
};

const denyLeadAccess = (roles) => {
  if (roles.lead) {
    forcefullyDisableNodeTree(roles.lead);
  }
  roles.lead = { view: false, edit: false };
};

const applyReadOnlyLeadPermissions = (roles) => {
  if (!roles.lead) roles.lead = {};
  roles.lead.view = true;
  roles.lead.edit = false;
  roles.lead.quickEdit = { view: false, edit: false };
  denyLeadImport(roles);
  if (roles.lead.lead) {
    roles.lead.lead.view = true;
    roles.lead.lead.edit = false;
  }
};

const applyFullLeadPermissions = (roles) => {
  if (!roles.lead) roles.lead = {};
  roles.lead.view = true;
  roles.lead.edit = true;
  denyLeadImport(roles);
};

const applyNoSettingsAccess = (roles) => {
  if (roles.settings) {
    forcefullyDisableNodeTree(roles.settings);
  }
  roles.settings.view = false;
  roles.settings.edit = false;
  roles.settings.userProfile = { view: true, edit: true };
  roles.settings.smsPreference = { view: false, edit: false };
  roles.settings.smsNumber = { view: false, edit: false };
};

const applyTeamManagerSettings = (roles) => {
  roles.settings.view = true;
  roles.settings.edit = true;
  denyOrgSettings(roles);
};

const disableAnalyticsEntirely = (roles) => {
  if (roles.analytics) {
    forcefullyDisableNodeTree(roles.analytics);
  }
  roles.analytics.view = false;
};

/** Appointment-only product: Stats page only (no CRM dashboard/reports). */
const applyAppointmentAnalyticsOnly = (roles, { readonly = false } = {}) => {
  if (!roles.analytics) roles.analytics = {};
  roles.analytics.view = true;
  roles.analytics.dashboard = { view: false, edit: false };
  roles.analytics.reports = { view: false, edit: false };
  roles.analytics.editor = { view: false, edit: false };
  roles.analytics["appointments-analytics"] = {
    view: true,
    edit: !readonly,
  };
  return roles;
};

const applyAppointmentOnlyCrmAdminPermissions = (roles) => {
  applyFullLeadPermissions(roles);
  roles.appointments = buildAppointmentPermissions("full");
  applyTeamManagerSettings(roles);
  roles.settings.rolesAndPermissions = { view: true, edit: true };
  roles.settings.smsNumber = { view: true, edit: true };
  roles.analytics.view = true;
  roles.analytics.reports = { view: true, edit: true };
  roles.analytics.dashboard = { view: false, edit: false };
  roles.analytics.editor = { view: false, edit: false };
  roles.analytics["appointments-analytics"] = { view: true, edit: true };
};

const applyAppointmentOnlyFallbackPermissions = (roles) => {
  // Mirror team-manager appointment-only access (e.g. SalesManager).
  applyFullLeadPermissions(roles);
  roles.appointments = buildAppointmentPermissions("full");
  applyTeamManagerSettings(roles);
  applyAppointmentAnalyticsOnly(roles);
};

const applyAppointmentOnlyReadonlyPermissions = (roles) => {
  applyReadOnlyLeadPermissions(roles);
  roles.appointments = buildAppointmentPermissions("readonly");
  roles.settings.view = true;
  roles.settings.edit = false;
  denyOrgSettings(roles);
  roles.settings.userProfile = { view: true, edit: true };
  applyAppointmentAnalyticsOnly(roles, { readonly: true });
};

// ---------------------------------------------------------------------------
// Legacy plan limits (standard CRM users only)
// ---------------------------------------------------------------------------

const applySubscriptionPlanLimits = (roles, user) => {
  const features = FeatureService.resolveFeatures(user);
  const plan = FeatureService.derivePlan(features);

  const allowedModules = PlanAllowedModules[plan] || PlanAllowedModules.basic;
  const deniedSubFeatures =
    PlanDeniedSubFeatures[plan] || PlanDeniedSubFeatures.basic;

  if (!allowedModules.includes("all")) {
    const restrictedModules = AllFeatures.filter(
      (feature) => !allowedModules.includes(feature)
    );
    restrictedModules.forEach((feature) => {
      if (roles[feature]) {
        forcefullyDisableNodeTree(roles[feature]);
      } else {
        createNestedObjectFromStringAndApplyValue(
          roles,
          feature,
          false,
          "view"
        );
        createNestedObjectFromStringAndApplyValue(
          roles,
          feature,
          false,
          "edit"
        );
      }
    });
  }

  deniedSubFeatures.forEach((featurePath) => {
    applyExactRoleValue(roles, featurePath, false);
  });

  // Appointment Stats is appointment-product only, not CRM.
  if (FeatureService.hasFeature(features, FeatureKeys.CRM)) {
    if (!roles.analytics) roles.analytics = {};
    roles.analytics["appointments-analytics"] = { view: false, edit: false };
  }

  return roles;
};

// ---------------------------------------------------------------------------
// Administrator & sales-manager base grants
// ---------------------------------------------------------------------------

const AdministratorPermissions = {
  Get: (roles, roleType) => {
    roles = applyAdminRoles(roles);
    return roles;
  },
};

const applyReadOnlyRights = (roles) => {
  roles.analytics.view = true;
  roles.analytics.dashboard.view = true;
  roles.analytics.reports.view = true;
  roles.appointments.view = true;
  roles.messaging.view = true;
  roles.conversation.view = true;
  roles.onBoarding.view = true;
  roles.lead.view = true;
  roles.list.view = true;
  roles.automation.template.view = true;
  roles.automation.automations.view = true;
  roles.sales.tasks.view = true;
  roles.settings.view = true;
  roles.settings.leadStatus.view = true;
  roles.settings.leadTags.view = true;
  roles.settings.taskStatus.view = true;
  roles.settings.taskType.view = true;
  roles.settings.taskPriority.view = true;
  roles.settings.taskReminder.view = true;
  roles.settings.customField.view = true;
  roles.settings.smsTemplate.view = true;
  roles.settings.apiKeys.view = true;
  roles.settings.emailTemplate.view = true;
  roles.settings.emailSignature.view = true;
  roles.settings.rolesAndPermissions.view = true;
  roles.settings.generalEmailSetting.view = true;
  roles.settings.advanceEmailSetting.view = true;
  roles.settings.smsPreference.view = true;
  roles.settings.smsNumber.view = true;
  return roles;
};

const administratorGrantedRoles = [
  "analytics",
  "analytics.dashboard",
  "analytics.reports",
  "appointments",
  "messaging",
  "conversation",
  "onBoarding",
  "lead",
  "lead.import",
  "lead.quickEdit",
  "list",
  "automation.template",
  "automation.automations",
  "sales.tasks",
  "settings",
  "settings.leadStatus",
  "settings.leadSource",
  "settings.leadTags",
  "settings.customField",
  "settings.smsTemplate",
  "settings.apiKeys",
  "settings.emailTemplate",
  "settings.emailSignature",
  "settings.rolesAndPermissions",
  "settings.generalEmailSetting",
  "settings.advanceEmailSetting",
  "settings.taskStatus",
  "settings.taskType",
  "settings.taskPriority",
  "settings.taskReminder",
  "settings.smsPreference",
  "settings.smsNumber",
];

const applyAdminRoles = (roles) => {
  roles = allowGrantedRolesForView(roles, administratorGrantedRoles);
  roles = allowGrantedRolesForEdit(roles, administratorGrantedRoles);
  return roles;
};

const applySpecialFieldsPermissions = (roles, userRoles) => {
  for (let entity in roles) {
    if (roles[entity].specialFields) {
      let keyArray = Object.keys(roles[entity].specialFields);
      keyArray.forEach((key) => {
        switch (key) {
          case "billingEmailAddress":
          case "invoiceEmail":
            if (
              (roles.isAdminCompany &&
                (userRoles.includes(Enums.Roles.BillingManager) ||
                  userRoles.includes(Enums.Roles.Administrator) ||
                  userRoles.includes(Enums.Roles.SuperAdministrator))) ||
              (!roles.isAdminCompany && roles.isDefaultUser)
            ) {
              roles[entity].specialFields[key].view = true;
              roles[entity].specialFields[key].edit = true;
            }
            break;
          case "provisionedCompanyId":
            if (
              roles.isAdminCompany &&
              (userRoles.includes(Enums.Roles.LimitedAdministrator) ||
                userRoles.includes(Enums.Roles.Administrator) ||
                userRoles.includes(Enums.Roles.SuperAdministrator))
            ) {
              roles[entity].specialFields[key].view = true;
              roles[entity].specialFields[key].edit = true;
            }
            break;
          default:
            break;
        }
      });
    }
  }
  return roles;
};

const allowGrantedRolesForView = (roles, allowedRoleArray) => {
  allowedRoleArray.forEach((roleName) => {
    roles = createNestedObjectFromStringAndApplyValue(
      roles,
      roleName,
      true,
      "view"
    );
  });
  return roles;
};

const allowGrantedRolesForEdit = (roles, allowedRoleArray) => {
  allowedRoleArray.forEach((roleName) => {
    roles = createNestedObjectFromStringAndApplyValue(
      roles,
      roleName,
      true,
      "edit"
    );
  });

  return roles;
};

const denyGrantedRolesForEdit = (roles, allowedRoleArray) => {
  allowedRoleArray.forEach((roleName) => {
    roles[roleName].edit = false;
  });
  return roles;
};

// ---------------------------------------------------------------------------
// Tree utilities
// ---------------------------------------------------------------------------

const forcefullyDisableNodeTree = (node) => {
  if (typeof node !== "object" || node === null) return;

  if (node.hasOwnProperty("view")) node.view = false;
  if (node.hasOwnProperty("edit")) node.edit = false;

  Object.values(node).forEach((child) => {
    forcefullyDisableNodeTree(child);
  });
};

const applyExactRoleValue = (roles, stringPath, value) => {
  const parts = stringPath.split(".");
  let currentObj = roles;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!currentObj[part]) {
      currentObj[part] = {};
    }
    currentObj = currentObj[part];
  }
  const lastKey = parts[parts.length - 1];
  currentObj[lastKey] = value;
  return roles;
};

const getDefaultRoles = () => {
  return {
    analytics: {
      view: true,
      dashboard: { view: false, edit: false },
      reports: { view: false, edit: false },
      editor: { view: true, edit: true },
      "appointments-analytics": { view: false, edit: false },
    },
    messaging: {
      view: false,
      edit: false,
    },
    conversation: {
      view: false,
      edit: false,
    },
    onBoarding: {
      view: false,
      edit: false,
    },
    lead: {
      view: false,
      edit: false,
    },
    list: {
      view: false,
      edit: false,
    },
    appointments: {
      view: false,
      edit: false,
    },
    automation: {
      view: true,
      template: { view: false, edit: false },
      automations: { view: false, edit: false },
    },
    sales: {
      view: true,
      tasks: { view: false, edit: false },
    },
    settings: {
      view: true,
      edit: false,
      leadStatus: { view: false, edit: false },
      leadSource: { view: false, edit: false },
      leadTags: { view: false, edit: false },
      customField: { view: false, edit: false },
      leadScoring: { view: false, edit: false },
      leadSettings: { view: false, edit: false },
      generalEmailSetting: { view: false, edit: false },
      advanceEmailSetting: { view: false, edit: false },
      smsTemplate: { view: false, edit: false },
      apiKeys: { view: false, edit: false },
      emailTemplate: { view: false, edit: false },
      emailSignature: { view: false, edit: false },
      rolesAndPermissions: { view: false, edit: false },
      taskStatus: { view: false, edit: false },
      taskType: { view: false, edit: false },
      taskPriority: { view: false, edit: false },
      taskReminder: { view: false, edit: false },
      smsPreference: { view: false, edit: false },
      smsNumber: { view: false, edit: false },
    },
  };
};

export default RoleEngine;

function createNestedObjectFromStringAndApplyValue(
  roles,
  string,
  value,
  permission = "view"
) {
  const parts = string.split(".");
  let currentObj = roles;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];

    if (!currentObj[part]) {
      currentObj[part] = {};
    }

    currentObj = currentObj[part];
  }

  const lastKey = parts[parts.length - 1];
  if (typeof currentObj[lastKey] !== "object" || currentObj[lastKey] === null) {
    if (currentObj[lastKey] !== undefined) {
      currentObj[lastKey] = { view: false, edit: false };
    } else {
      currentObj[lastKey] = {};
    }
  }

  currentObj[lastKey][permission] = value;

  return roles;
}
